import {
  createAsyncThunk,
  createAction,
  ActionReducerMapBuilder,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';

import {
  DashboardService,
  GetSocialWidgetListResponse,
} from '@app/gen/dashboard';
import { ThunkExtra } from '@app/store';
import { Entity } from '@app/types';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';

import { SettingsState } from '../../../slice';
import { GetWidgetsContentDto } from '../../../dto';

const setWidgetListFilters = createAction<Filter[] | null>(
  'widgetsContent/setFilters',
);

export const getWidgetList = createAsyncThunk<
  GetSocialWidgetListResponse,
  GetWidgetsContentDto,
  ThunkExtra
>(
  'widgetsContent/getWidgetList',
  async (
    getWidgetsContentDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);
      const state = getState().settings.widgetContent;

      let filters = state.filters;
      const filterScopeType = state.filterScopeType;

      if (!filters || filterScopeType !== getWidgetsContentDto.scope_type) {
        filters = (
          await service.getWidgetListAvailableFilters({
            query: { bind_type: getWidgetsContentDto.scope_type },
          })
        ).filters;
        dispatch(setWidgetListFilters(filters));
      }

      const result = await service.getSocialWidgetList({
        body: {
          ...getWidgetsContentDto.filters,
          offset: getWidgetsContentDto.offset,
          limit: getWidgetsContentDto.limit,
          bind_type: getWidgetsContentDto.scope_type,
        },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsWidgetContentReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(setWidgetListFilters, (state, action) => {
    state.widgetContent.filters = action.payload;
  });

  builder.addCase(getWidgetList.pending, (state) => {
    state.widgetContent.status = 'loading';
  });

  builder.addCase(getWidgetList.fulfilled, (state, { payload, meta }) => {
    state.widgetContent.data = payload.widgets;
    state.widgetContent.total = payload.widgets_total;
    state.widgetContent.accounts = payload.accounts;
    state.widgetContent.companies = payload.companies;
    state.widgetContent.status = 'complete';
    state.widgetContent.filterScopeType = meta.arg.scope_type;
  });

  builder.addCase(getWidgetList.rejected, (state, { payload, error }) => {
    state.widgetContent.status = 'failed';
    state.widgetContent.error =
      payload ||
      ({
        info: (error && error.message) || 'Что-то пошло не так',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
};
